import Vue from 'vue'
import Router, { Route } from 'vue-router'
import RouteName from '@/utils/RouteName'
import { redirectToEventLoginIfNotConnected, redirectToEventPayment } from '@/utils/router-middlewares'
import guard from '@/utils/guard'
import RouterPrefetch from 'vue-router-prefetch'
import NoLayout from '@/layouts/NoLayout.vue'
import PrivateLayoutBasic from '@/layouts/PrivateLayoutBasic.vue'
import store from '@/store/store'
import AuthLayout from '@/layouts/AuthLayout.vue'
import UpdateAccountLayout from '@/layouts/auth/UpdateAccountLayout.vue'
import CommunityApplyLayout from '@/layouts/CommunityApplyLayout.vue'
import { ApplyStep } from '@/utils/ApplyAttr'
import loginAndRedirect from './middleware/login-and-redirect'

Vue.use(Router)
Vue.use(RouterPrefetch)

const router = new Router({
    mode: 'history',
    base: process.env.VUE_APP_ROUTER_BASE_URL || '/',
    routes: [
        {
            path: '/spa/app/login-and-redirect',
            name: RouteName.LOGIN_AND_REDIRECT,
            beforeEnter: loginAndRedirect,
        },
        {
            path: '/events',
            name: RouteName.EVENTS,
            beforeEnter(to) {
                let finalRoute = `${process.env.VUE_APP_CLUB_BASE_URL}/events`
                const urlSearchParams = new URLSearchParams(to.query as Record<string, string>)

                if (urlSearchParams.toString()) {
                    finalRoute += '?' + urlSearchParams.toString()
                }

                window.location.href = finalRoute
            },
        },
        {
            path: '/networking-room',
            name: RouteName.NETWORKING_ROOM,
            component: () => import(/* webpackChunkName: 'networking' */ './views/networking/NetworkingRoomPage.vue'),
            meta: { layout: NoLayout },
        },
        {
            path: '/public/cgv',
            name: RouteName.PUBLIC_CGV,
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_BASE_URL}/page/cgv`
            },
            meta: {
                layout: NoLayout,
            },
        },
        {
            path: '/events/video',
            name: RouteName.EVENTS_VIDEO,
            component: () => import(/* webpackChunkName: 'events-video' */ '@/views/event/list/EventVideoListPage.vue'),
            meta: {
                layoutProps: {
                    extended: 'xl',
                },
            },
        },
        {
            path: '/events/live-and-replay',
            name: RouteName.EVENTS_LIVE_AND_REPLAY,
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/events/live-and-replay`
            },
        },
        {
            path: '/public/test',
            name: RouteName.PUBLIC_TEST,
            component: () => import(/* webpackChunkName: 'public-test' */ './views/test/TestPage.vue'),
            meta: { layout: NoLayout },
        },
        {
            path: '/event/payment/:eventId',
            name: 'event-payment',
            redirect: to => {
                redirectToEventPayment(to)
                return ''
            },
            //component: () => import(/* webpackChunkName: 'event-payment' */ './views/event/EventPaymentPage.vue'),
            meta: {
                doNotResetLocale: true,
            },
        },
        {
            path: '/public/event/direct-payment/:eventId',
            name: 'event-payment',
            redirect: to => {
                redirectToEventPayment(to)
                return ''
            },
            //component: () => import(/* webpackChunkName: 'event-payment' */ '@/views/event/EventDirectPaymentPage.vue'),
            meta: {
                doNotResetLocale: true,
            },
        },
        {
            path: '/public/event/payment/:eventId',
            name: 'public-event-payment',
            redirect: to => {
                redirectToEventPayment(to)
                return ''
            },
            // component: () =>
            //    import(/* webpackChunkName: 'public-event-payment' */ './views/event/EventPaymentPage.vue'),

            meta: {
                doNotResetLocale: true,
            },
        },
        {
            path: '/event/:eventId/:eventSlug',
            name: RouteName.EVENT,
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/event/${to.params.eventId}/${to.params.eventSlug}`
            },
            meta: {
                layoutProps: {
                    transparent: true,
                },
                doNotResetLocale: true,
            },
        },
        {
            path: '/public/event/:eventId/:eventSlug',
            name: RouteName.PUBLIC_EVENT,
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/event/${to.params.eventId}/${to.params.eventSlug}`
            },
            meta: {
                layoutProps: {
                    transparent: true,
                },
                doNotResetLocale: true,
            },
        },
        {
            path: '/event/:eventId/:eventSlug/replay',
            name: RouteName.EVENT_REPLAY,
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/event/${to.params.eventId}/${to.params.eventSlug}/replay`
            },
            meta: {
                layoutProps: {
                    transparent: true,
                },
                doNotResetLocale: true,
            },
        },
        {
            path: '/test',
            name: RouteName.TEST,
            component: () => import(/* webpackChunkName: 'test-page' */ './views/test/TestPage.vue'),
            meta: { layout: NoLayout, allowContactPartner: true },
        },
        {
            path: '/invite/:invitationCode',
            name: 'invitation',
            component: () => import(/* webpackChunkName: 'invitation' */ './views/apply/steps/ApplyContact.vue'),
            meta: {
                layout: NoLayout,
                allowCrisp: true,
                allowLead: true,
            },
        },
        {
            path: '/v3/apply',
            name: RouteName.COMMUNITY_APPLY,
            component: () => import(/* webpackChunkName: 'community-apply' */ './views/community-apply/Apply.vue'),
            meta: { layout: CommunityApplyLayout, step: ApplyStep.PROFILE, allowCrisp: true },
        },
        {
            path: '/v3/apply/membership',
            name: RouteName.COMMUNITY_APPLY_MEMBERSHIP,
            component: () => import(/* webpackChunkName: 'community-apply' */ './views/community-apply/Apply.vue'),
            meta: { layout: CommunityApplyLayout, step: ApplyStep.MEMBERSHIP, allowCrisp: true },
        },
        {
            path: '/v3/apply/payment',
            name: RouteName.COMMUNITY_APPLY_PAYMENT,
            component: () => import(/* webpackChunkName: 'community-apply' */ './views/community-apply/Apply.vue'),
            meta: { layout: CommunityApplyLayout, step: ApplyStep.PAYMENT, allowCrisp: true },
        },
        {
            path: '/v3/apply/validation',
            name: RouteName.COMMUNITY_APPLY_VALIDATION,
            component: () => import(/* webpackChunkName: 'community-apply' */ './views/community-apply/Apply.vue'),
            meta: { layout: CommunityApplyLayout, step: ApplyStep.VALIDATION, allowCrisp: true },
        },
        {
            path: '/v3/apply/finished',
            name: RouteName.COMMUNITY_APPLY_FINISHED,
            component: () =>
                import(/* webpackChunkName: 'community-apply' */ './views/community-apply/ApplyFinished.vue'),
            meta: { layout: CommunityApplyLayout, step: ApplyStep.FINISHED, allowCrisp: true },
        },
        {
            path: '/apply',
            name: RouteName.APPLY_ROOT,
            component: () => import(/* webpackChunkName: 'apply-router' */ './views/apply/ApplyRouter.vue'),
            meta: { layout: NoLayout },
            children: [
                {
                    name: RouteName.APPLY,
                    path: 'contact',
                    component: () =>
                        import(/* webpackChunkName: 'apply-contact' */ './views/apply/steps/ApplyContact.vue'),
                    meta: {
                        layout: NoLayout,
                        allowCrisp: true,
                        allowLead: true,
                        gtag: {
                            event: 'Apply_Profil_Step1',
                            flow: window?.payload?.flow || 'regular',
                        },
                    },
                },
                {
                    name: RouteName.APPLY_MEMBERSHIP,
                    path: 'membership/:leadId',
                    component: () =>
                        import(/* webpackChunkName: 'apply-membership' */ '@/views/apply/steps/ApplyMembership.vue'),
                    meta: {
                        layout: NoLayout,
                        allowCrisp: true,
                        allowLead: true,
                        gtag: {
                            event: 'Apply_Profil_Step2',
                            flow: window?.payload?.flow || 'regular',
                        },
                    },
                },
                {
                    name: RouteName.APPLY_DETAILS,
                    path: 'details/:leadId',
                    component: () =>
                        import(/* webpackChunkName: 'apply-details' */ './views/apply/steps/ApplyDetails.vue'),
                    meta: {
                        layout: NoLayout,
                        allowCrisp: true,
                        allowLead: true,
                        gtag: {
                            event: 'Apply_Profil_Step3',
                            flow: window?.payload?.flow || 'regular',
                        },
                    },
                    props: true,
                },
                {
                    name: RouteName.APPLY_VALIDATION,
                    path: 'validation/:leadId',
                    component: () =>
                        import(/* webpackChunkName: 'apply-validation' */ './views/apply/steps/ApplyValidation.vue'),
                    meta: {
                        layout: NoLayout,
                        allowCrisp: true,
                        allowLead: true,
                        gtag: {
                            event: 'Apply_Profil_Step4',
                            flow: window?.payload?.flow || 'regular',
                        },
                    },
                },
                {
                    name: RouteName.APPLY_FINISHED,
                    path: 'finished/:leadId',
                    component: () =>
                        import(/* webpackChunkName: 'apply-finished' */ './views/apply/steps/ApplyFinished.vue'),
                    meta: {
                        layout: NoLayout,
                        allowCrisp: true,
                        allowLead: true,
                        gtag: {
                            event: 'Apply_Profil_Step5',
                            flow: window?.payload?.flow || 'regular',
                        },
                    },
                },
            ],
        },
        {
            path: '/members',
            name: 'members',
            redirect: {
                name: RouteName.MEMBER_LIST_ALL,
            },
            children: [
                {
                    name: RouteName.MEMBER_LIST_ALL,
                    path: 'all',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/members/all`
                    },
                },
                {
                    name: RouteName.MEMBER_LIST_CONTACTS,
                    path: 'contacts',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/members/contacts`
                    },
                },
                {
                    name: RouteName.MEMBER_LIST_FAVORITES,
                    path: 'favorites',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/members/favorites`
                    },
                },
                {
                    name: RouteName.MEMBER_LIST_SUGGESTIONS,
                    path: 'suggestions',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/members/suggestions`
                    },
                },
            ],
        },
        {
            path: '/member/recommend',
            name: RouteName.MEMBER_RECOMMEND,
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/recommend`
            },
        },
        {
            path: '/member/me',
            component: () => import(/* webpackChunkName: 'member-single' */ '@/views/member/MemberProfileProvider.vue'),
            children: [
                {
                    name: RouteName.MEMBER_ME,
                    path: '',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/me`
                    },
                },
                {
                    name: RouteName.MEMBER_AGREEMENT_ME,
                    path: 'agreement',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/me/agreement`
                    },
                },
                {
                    name: RouteName.MEMBER_BUSINESS_NEEDS,
                    path: 'business-needs',
                    component: () =>
                        import(
                            /* webpackChunkName: 'member-single' */ '@/views/member/profile/MemberBusinessNeeds.vue'
                        ),
                },
                {
                    name: RouteName.MEMBER_PREFERENCES,
                    path: 'preferences',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/me/preferences`
                    },
                },
            ],
        },
        {
            path: '/member/:memberId',
            alias: '/member-front/:memberId',
            component: () => import(/* webpackChunkName: 'member-single' */ '@/views/member/MemberProfileProvider.vue'),
            children: [
                {
                    name: RouteName.MEMBER,
                    path: '',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/${to.params.memberId}`
                    }
                },
                {
                    name: RouteName.MEMBER_AGREEMENT,
                    path: 'agreement',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/member/${to.params.memberId}/agreement`
                    },
                },
                {
                    name: RouteName.MEMBER_AGREEMENT_HISTORY,
                    path: 'history',
                    component: () =>
                        import(/* webpackChunkName: 'member-single' */ '@/views/member/profile/AgreementHistory.vue'),
                },
            ],
            meta: {
                anonymousForbidden: true,
                allowContactPartner: true,
            },
        },
        {
            name: RouteName.EVENT_BACKSTAGE,
            path: '/backstage/:eventId',
            beforeEnter: redirectToEventLoginIfNotConnected,
            meta: {
                allowLead: true,
                layout: PrivateLayoutBasic,
                layoutProps: {
                    extended: 'xl',
                    transparent: true,
                },
                doNotResetLocale: true,
            },
            component: () =>
                import(/* webpackChunkName: 'event-backstage' */ './views/event/backstage/EventBackstage.vue'),
        },
        {
            name: RouteName.EVENT_LIVE_MANUAL_LOGIN,
            path: '/login-event/:eventId',
            meta: {
                allowLead: true,
                allowCrisp: true,
                layoutProps: {
                    extended: 'sm',
                },
                doNotResetLocale: true,
            },
            component: () =>
                import(
                    /* webpackChunkName: 'event-live-manual-login' */ './views/event/EventLivestreamManualLogin.vue'
                ),
        },
        {
            name: RouteName.EVENT_LIVE,
            path: '/live/:eventId',
            component: () => import(/* webpackChunkName: 'event-live' */ './views/event/live/EventLive.vue'),
            beforeEnter: redirectToEventLoginIfNotConnected,
            redirect: {
                name: 'event-live-schedule',
            },
            meta: {
                allowLead: true,
                layoutProps: {
                    transparent: true,
                },
                doNotResetLocale: true,
            },
            children: [
                {
                    name: RouteName.EVENT_LIVE_SCHEDULE,
                    path: 'reception',
                    meta: {
                        allowLead: true,
                        allowCrisp: true,
                        doNotResetLocale: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: 'event-live-schedule' */ '@/views/event/live/EventLive/EventLiveSchedule.vue'
                        ),
                },
                {
                    name: RouteName.EVENT_LIVE_STAGE,
                    path: 'stage/:stageId',
                    meta: {
                        allowLead: true,
                        allowCrisp: true,
                        doNotResetLocale: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: 'event-live-stage' */ '@/views/event/live/EventLive/EventLiveStageTab.vue'
                        ),
                },
                {
                    name: RouteName.EVENT_LIVE_NETWORKING,
                    path: 'networking',
                    meta: {
                        allowLead: true,
                        allowCrisp: true,
                        doNotResetLocale: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: 'event-live-networking' */ '@/views/event/live/EventLive/EventLiveNetworking.vue'
                        ),
                },
                {
                    name: 'event-live-partners',
                    path: 'partners',
                    component: () =>
                        import(
                            /* webpackChunkName: 'event-live-partners' */ '@/views/event/live/EventLive/EventLivePartners.vue'
                        ),
                    meta: {
                        allowLead: true,
                        bnp_logo: require('@/assets/images/partners/BNPP_noir.png'),
                        allowCrisp: true,
                        doNotResetLocale: true,
                    },
                },
                {
                    name: 'event-live-partner-bnp-paribas',
                    path: 'partner/bnp-paribas',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/KVc48lqRkfA',
                        logo: require('@/assets/images/partners/BNPP_noir.png'),
                        content:
                            'BNP Paribas Wealth Management is a leading global private bank and the largest private bank in the Eurozone with €359 billion worth of assets under management. Present in Europe, Asia, the Middle East and in the US, more than 7.000 professionals provide a private investor clientele with solutions for growing and transferring their wealth by creating long-term value for them, their family and the society in general. BNP Paribas Wealth Management’s vision is to help clients achieve positive impact with their wealth through a personalized approach and ambitions in line with the UN’s Sustainable Development Goals.',
                        twitter: 'https://twitter.com/BNPParibas',
                        linkedin: 'https://www.linkedin.com/company/bnp-paribas',
                        contactMember: {
                            id: 42997,
                            idff: 'FF-3320802220',
                            full_name: 'Eléonore Bedel',
                            picture: require('@/assets/images/partners_contacts/e_bedel.png'),
                            position: 'Wealth Management',
                            email: 'eleonore.bedel@bnpparibas.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 7873,
                        // contact: 'Geraud Haissat',
                        // contact_job: 'Managing Director',
                        // contact_photo: require('@/assets/images/partners_contacts/g_haissat.jpg'),
                        website: 'https://group.bnpparibas/en/',
                    },
                },
                {
                    name: 'event-live-partner-orbiss-1',
                    path: 'partner/orbiss-1',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/zScRK7ENDTc',
                        logo: require('@/assets/images/partners/orbiss.png'),
                        content:
                            "Orbiss is an advisory and tax firm specialized in business growth in the United States. Orbiss helps international small and medium-sized private companies successfully scale up in the United States. The firm offers a 360 °and tailor-made support to its clients' cross-border accounting, tax and M&A transaction needs. The Orbiss team’s vision is modernity. This vision is implemented through an innovative approach focusing on processes automation and aiming to place people at the heart of our concerns.",
                        twitter: '',
                        linkedin: 'https://www.linkedin.com/company/orbiss-inc/',
                        contactMember: {
                            id: 31861,
                            idff: 'FF-6848606280',
                            full_name: 'Laurence Ruiz',
                            position: 'Founder',
                            picture: require('@/assets/images/partners_contacts/l_ruiz.jpeg'),
                            email: 'laurence@orbiss.us',
                        },
                        doNotResetLocale: true,
                        // contact_id: 31861,
                        // contact: 'Laurence Ruiz',
                        // contact_job: 'Founder',
                        // contact_photo: require('@/assets/images/partners_contacts/l_ruiz.jpeg'),
                        website: 'www.orbiss.com',
                    },
                },
                {
                    name: 'event-live-partner-theodo',
                    path: 'partner/theodo',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/UdJs0B1ifec',
                        logo: require('@/assets/images/partners/theodo.png'),
                        content:
                            'Theodo is a consultancy and digital development agency, founded by Benoît Charles-Lavauzelle and Fabrice Bernhard in 2009. Today we number 200 in total, based in London, Paris and New York.<br>' +
                            'We build custom web and mobile apps with our clients to increase their market share and productivity. Our team of experts bring our clients into the development process from start to finish, delivering tangible solutions as early as possible in the process.',
                        twitter: 'http://twitter.com/theodo',
                        linkedin: 'http://www.linkedin.com/company/theodo',
                        contactMember: {
                            id: 26818,
                            idff: 'FF-2064048804',
                            full_name: 'Nicolas Taborisky',
                            position: 'US CEO',
                            picture: require('@/assets/images/partners_contacts/n_taborisky.png'),
                            email: 'nt@theodo.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 26818,
                        // contact: 'Nicolas Taborisky',
                        // contact_job: 'US CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/n_taborisky.png'),
                        website: 'https://www.theodo.fr/',
                    },
                },
                {
                    name: 'event-live-partner-colombus-consulting',
                    path: 'partner/colombus-consulting',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        image: require('@/assets/images/partners_photos/columbus_video.png'),
                        logo: require('@/assets/images/partners/columbus.png'),
                        content:
                            'With a strong focus on immigration services, Columbus Consulting Group assists individuals and companies in their expansion in the United States. Columbus has an extensive scope of services to cover all business needs of foreign companies and entrepreneurs who are looking to make a successful, quick and easy entry into the U.S. market.',
                        twitter: 'https://twitter.com/Columbus_Group',
                        linkedin: 'https://www.linkedin.com/company/columbusconsultinggroup-ccg/',
                        contactMember: {
                            id: 1126,
                            idff: 'FF-6836870141',
                            full_name: 'Isabelle Marcus',
                            position: 'Founder & CEO',
                            picture: require('@/assets/images/partners_contacts/i_marcus.jpeg'),
                            email: 'isabel@columbusconsulting-group.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 1126,
                        // contact: 'Isabelle Marcus',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/i_marcus.jpeg'),
                        website: 'https://www.columbusconsultinggroup.com/',
                    },
                },
                {
                    name: 'event-live-partner-eleius',
                    path: 'partner/eleius',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        image: require('@/assets/images/partners_photos/eleius_video.png'),
                        logo: require('@/assets/images/partners/eleius.png'),
                        content:
                            'Eleius, Your Full Service B2B Growth Agency<br><br>' +
                            'Eleius is your team of expert international B2B marketers you can rely on to accelerate your global growth. We are practitioners of the whole range of methods that have evolved in support of the world’s fastest growing B2B companies. We have helped over forty start-ups, scale-ups and established businesses execute winning marketing strategies, communicate with impact, and generate sales leads at scale.',
                        //twitter: '',
                        linkedin: 'https://www.linkedin.com/company/eleius/',
                        contactMember: {
                            id: 43004,
                            idff: 'FF-2560062084',
                            full_name: 'Etienne Viellard',
                            position: 'CEO',
                            picture: require('@/assets/images/partners_contacts/e_viellard.png'),
                            email: 'eviellard@eleius.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 1126,
                        // contact: 'Isabelle Marcus',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/i_marcus.jpeg'),
                        website: 'https://www.eleius.com/',
                    },
                },
                {
                    name: 'event-live-partner-creative-bot',
                    path: 'partner/creative_bot',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        image: require('@/assets/images/partners_photos/creative_bot_video.png'),
                        logo: require('@/assets/images/partners/creative_bot.png'),
                        content:
                            'Creative Bot helps large sports organizations to capture more of their digital fan base through direct messaging like SMS, Facebook Messenger and WhatsApp.\n' +
                            '\n' +
                            'We empower sports teams to build meaningful relationships with their fans at scale.',
                        twitter: 'https://twitter.com/creativebothq',
                        linkedin: 'https://www.linkedin.com/company/creative-bot/about/',
                        contactMember: {
                            id: 11556,
                            idff: 'FF-2466240620',
                            full_name: 'Benjamin Benichou',
                            position: 'Co-Founder & CEO',
                            picture: require('@/assets/images/partners_contacts/b_benichou.jpeg'),
                            email: 'benjamin@creativebot.ai',
                        },
                        doNotResetLocale: true,
                        // contact_id: 11556,
                        // contact: 'Benjamin Benichou',
                        // contact_job: 'Co-Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_benichou.jpeg'),
                        website: 'https://www.creativebot.ai/',
                    },
                },
                {
                    name: 'event-live-partner-joorney-business-plan',
                    path: 'partner/joorney-business-plan',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/R_ehZYTWhco',
                        logo: require('@/assets/images/partners/joorney.png'),
                        content:
                            'Joorney Business Plans offers a full suite of services from business plan writing and pitch decks to strategic advisory and consulting services. We have a solid track record in writing high-quality business plans and financial models that support business owners and startups in raising capital and debt financing. We know what investors look for in business plans & pitch decks and how to communicate with them effectively. We’ve helped over 200 businesses in market research and feasibility studies, creating financial models and pitch decks, and providing strategic advice that has generated over $200m in funding. Let us help you achieve your business goals by sparking interest in what matters to you.',
                        twitter: 'https://twitter.com/joorney',
                        linkedin: 'https://www.linkedin.com/company/joorney-llc/',
                        contactMember: {
                            id: 795,
                            idff: 'FF-8894384041',
                            full_name: 'Benjamin Jarmon',
                            position: 'Founder & CEO',
                            picture: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                            email: 'bjarmon@joorney.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://www.joorney.com/',
                    },
                },
                {
                    name: 'event-live-partner-contentsquare',
                    path: 'partner/contentsquare',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/3ldyM4uca90',
                        logo: require('@/assets/images/partners/contentsquare.png'),
                        content:
                            'Contentsquare empowers brands to act on unique behavioral insights to turn experience into a measurable advantage. The leading experience analytics platform, Contentsquare transforms trillions of digital behaviors daily into intelligent visualizations and recommendations everyone can use to grow revenue, increase loyalty and fuel innovation. Contentsquare customers benefit from access to benchmarks and predictive scoring based on the leading global data set of digital behavior across geographies and industries. The company acquired Clicktale in 2019 and serves more than 600 enterprises worldwide, including 30% of the Fortune Global 100. For more information, visit contentsquare.com.',
                        twitter: 'https://twitter.com/ContentSquare',
                        linkedin: 'https://www.linkedin.com/company/contentsquare/',
                        contactMember: {
                            id: 5076,
                            idff: 'FF-7408203174',
                            full_name: 'Jonathan Cherki',
                            position: 'CEO',
                            picture: require('@/assets/images/partners_contacts/j_cherki.jpg'),
                            email: 'cherki.jonathan@contentsquare.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://contentsquare.com/',
                    },
                },
                {
                    name: 'event-live-partner-viseo',
                    path: 'partner/viseo',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/jzalww5gMb0',
                        logo: require('@/assets/images/partners/viseo.png'),
                        content:
                            'VISEO a global IT consulting firm specialized in assisting its clients with their IT and digital transformation. VISEO uses technology as a powerful lever of transformation and innovation to help its clients take advantage of digital opportunities, address new usages and compete with new players who change the rules of the game.\n' +
                            'With 2200 employees working on 5 continents, VISEO combines agility and complementarity of its areas of expertise – design of new products and services, digitization of business processes, ​​data valuation, digital assets development - to make digital a real lever of competitiveness and performance.',
                        twitter: 'https://twitter.com/viseogroup',
                        linkedin: 'https://www.linkedin.com/company/viseo/',
                        contactMember: {
                            id: 40813,
                            idff: 'FF-1006401280',
                            full_name: 'Eric Perrier',
                            position: 'Co-founder & CEO',
                            picture: require('@/assets/images/partners_contacts/e_perrier.jpg'),
                            email: 'eric.perrier@viseo.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://www.viseo.com/',
                    },
                },
                {
                    name: 'event-live-partner-launchmetrics-2',
                    path: 'partner/launchmetrics-2',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        image: require('@/assets/images/partners_photos/launchmetrics_video.jpg'),
                        logo: require('@/assets/images/partners/launchmetrics.png'),
                        content:
                            'Launchmetrics is the leading Brand Performance Cloud used by Fashion, Luxury and Beauty (FLB) executives to connect with the modern consumer in a constantly changing landscape. With over a decade of industry expertise, Launchmetrics helps more than 1,000 customers create inspiring, impactful and measurable experiences.\n' +
                            "Its Brand Performance Cloud provides companies with the tools and intelligence they need to optimize the use of their creative assets, execute powerful brand amplification programs and measure their brand performance. The company's AI-driven and proprietary Media Impact Value TM gives customers the ability to benchmark their performance against 2,000 competitors worldwide. Launchmetrics' intelligence empowers these companies to grow their businesses and streamline their processes, bringing a sharp focus to profitability, accountability, and efficiency while enabling the type of quick decision making required for agility.\n" +
                            'Founded in New York and with operating headquarters in Paris, Launchmetrics has employees in ten markets worldwide and offers support in five languages. Launchmetrics has been the trusted brand performance technology to brands worldwide such as Dior, Fendi, TopShop, Shiseido, NET-A-PORTER and Adidas as well as partners like IMG, the Council of Fashion Designers of America, the British Fashion Council, and Camera Nazionale Della Moda Italiana.',
                        twitter: 'https://twitter.com/launchmetrics',
                        linkedin: 'https://www.linkedin.com/company/launchmetrics',
                        contactMember: {
                            id: 2346,
                            idff: 'FF-8461686241',
                            full_name: 'Arnaud Roy',
                            position: 'Chief Strategy & Associé',
                            picture: require('@/assets/images/partners_contacts/a_roy.jpg'),
                            email: 'arnaud.roy@launchmetrics.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://www.launchmetrics.com/',
                    },
                },
                {
                    name: 'event-live-partner-la-french-tech',
                    path: 'partner/la-french-tech',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/csdgiVYrmuI',
                        logo: require('@/assets/images/partners/frenchtech.png'),
                        content:
                            '"La French Tech" is our startup scene here in France. It\'s also a unique movement bringing together startups, investors, policy makers and community builders. Our mission ? To make France one of the greatest places in the world to launch and grow global companies that make sense for our future.',
                        twitter: 'https://twitter.com/lafrenchtech',
                        linkedin: 'https://www.linkedin.com/company/la-french-tech',
                        doNotResetLocale: true,
                        /*contactMember: {
                            id: 40813,
                            full_name: 'Eric Perrier',
                            position: 'Co-founder & CEO',
                            picture: require('@/assets/images/partners_contacts/e_perrier.jpg'),
                            email: 'eric.perrier@viseo.com',
                        },*/
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://lafrenchtech.com/',
                    },
                },
                {
                    name: 'event-live-partner-bpifrance',
                    path: 'partner/bpifrance',
                    component: () => import('@/components/Partner/LivePartnerDetails.vue'),
                    meta: {
                        allowLead: true,
                        video: 'https://www.youtube.com/embed/GW1IK1nxIr8',
                        logo: require('@/assets/images/partners/bpi.png'),
                        content:
                            'Bpifrance’s goal is to favour the growth of the French economy by helping entrepreneurs thrive. We are the trusted partner of entrepreneurs and champion the interests of those who are visionaries; those who dare to take risks in order to take their businesses further. We have many resources at our disposal to help entrepreneurs, the champions of the 21st century, succeed. We mainly support micro-businesses, SMEs and mid-caps but we also accompany large caps that are considered important to the interests of France in terms of national economy, the territories or employment. We offer a continuum of solutions adapted to every key step in a business’ growth such as: business creation, financing, guarantees or equity investment.<br><br>' +
                            'We are also the French agency for innovation, delivering massive programs to innovative entrepreneurs. To put it simply: Bpifrance is a financial institution, with private culture, serving the collective interest. Our values are determination / optimism / proximity / simplicity and also performance: we generate significant returns to our shareholders.',
                        twitter: 'https://twitter.com/bpifrance',
                        linkedin: 'https://www.linkedin.com/company/bpifrance/',
                        contactMember: {
                            id: 40813,
                            idff: 'FF-1006401280',
                            full_name: 'Romain Serman',
                            position: '',
                            picture: require('@/assets/images/partners_contacts/r_serman.png'),
                            email: 'romain.serman@bpifrance.com',
                        },
                        doNotResetLocale: true,
                        // contact_id: 795,
                        // contact: 'Benjamin Jarmon',
                        // contact_job: 'Founder & CEO',
                        // contact_photo: require('@/assets/images/partners_contacts/b_jarmon.jpeg'),
                        website: 'https://www.bpifrance.com/',
                    },
                },
            ],
        },
        {
            name: RouteName.EVENT_LIVE_MAGIC_LINK,
            path: '/public/live/:eventId/:token',
            component: () => import(/* webpackChunkName: 'event-magic-link' */ './views/event/EventMagicLink.vue'),
            meta: {
                redirection: 'live',
                allowLead: true,
                layout: NoLayout,
                doNotResetLocale: true,
            },
        },
        {
            name: RouteName.EVENT_BACKSTAGE_MAGIC_LINK,
            path: '/public/backstage/:eventId/:token',
            component: () => import(/* webpackChunkName: 'event-magic-link' */ './views/event/EventMagicLink.vue'),
            meta: {
                redirection: 'backstage',
                allowLead: true,
                layout: NoLayout,
                doNotResetLocale: true,
            },
        },
        {
            path: '/v2/search/all/:search?',
            name: RouteName.SEARCH_SEARCH_ALL,
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/v2/search/all?search=${to.query.search}`
            },
            meta: {
                layoutProps: {
                    extended: 'sm',
                    menu: 'search-all',
                },
            },
        },
        {
            path: '/',
            name: 'home',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/`
            },
            meta: {
                layoutProps: {
                    extended: 'sm',
                },
            },
        },
        {
            path: '/gocardless-success',
            name: 'gocardless-success',
            component: () =>
                import(
                    /* webpackChunkName: 'membership' */ '@/views/member/membership/components/paymentMethods/GoCardLessSuccess.vue'
                ),
        },
        {
            path: '/member/me/membership',
            name: RouteName.MEMBERSHIP,
            component: () => import(/* webpackChunkName: 'membership' */ '@/views/member/membership/MyMembership.vue'),
            redirect: {
                name: 'membership-overview',
            },
            meta: {
                layoutProps: {
                    extended: 'xl',
                },
                allowLost: true,
            },
            children: [
                {
                    name: 'membership-overview',
                    path: 'overview',
                    beforeEnter() {
                        window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/account/membership`
                    },
                },
                {
                    name: 'update-billing-address',
                    path: 'billing-address',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'add-payment-method',
                    path: 'add-payment-method',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'update-payment-method',
                    path: 'update-payment-method/:paymentMethodId',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'renewal-membership',
                    path: 'renewal-membership',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'upgrade-membership',
                    path: 'upgrade-membership',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'renewal-addon',
                    path: 'renewal-addon/:addonSubscriptionId',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'update-membership',
                    path: 'update-membership',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'membership-auto-renewal',
                    path: 'membership-auto-renewal',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'addon-auto-renewal',
                    path: 'addon-auto-renewal/:addonSubscriptionId',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'favorite-payment-method',
                    path: 'favorite-payment-method',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'membership-unsubscribe',
                    path: 'membership-unsubscribe',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
                {
                    name: 'gocardless-success',
                    path: 'gocardless-success',
                    redirect: {
                        name: 'membership-overview',
                    },
                },
            ],
        },
        {
            name: RouteName.ROOM_CONFERENCE,
            path: '/room/:roomId',
            component: () => import(/* webpackChunkName: 'room' */ '@/views/room/RoomConference.vue'),
            meta: { allowLead: true, layout: NoLayout, doNotResetLocale: true },
        },
        {
            name: RouteName.LOGIN,
            path: '/login',
            beforeEnter: () => {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/login`
            },
            meta: {
                layout: AuthLayout,
                allowContactPartner: true,
            },
        },
        {
            name: RouteName.REGISTER,
            path: '/register',
            beforeEnter: () => {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/register`
            },
            meta: {
                layout: AuthLayout,
                allowCrisp: true,
            },
        },
        {
            path: '/update-account',
            component: () => import(/* webpackChunkName: 'auth' */ '@/views/auth/UpdateAccount.vue'),
            meta: {
                layout: UpdateAccountLayout,
                allowContactPartner: true,
            },
            children: [
                {
                    name: RouteName.UPDATE_ACCOUNT,
                    path: '',
                    component: () => import(/* webpackChunkName: 'auth' */ '@/views/auth/update/AuthUpdateProfile.vue'),
                    meta: {
                        layout: UpdateAccountLayout,
                        allowContactPartner: true,
                    },
                },
                {
                    name: RouteName.UPDATE_ACCOUNT_PASSWORD,
                    path: 'password',
                    component: () =>
                        import(/* webpackChunkName: 'auth' */ '@/views/auth/update/AuthUpdatePassword.vue'),
                    meta: {
                        layout: UpdateAccountLayout,
                        allowContactPartner: true,
                    },
                },
            ],
        },
        {
            name: RouteName.PASSWORD_FORGOT,
            path: '/resetting/request',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/resetting/request`
            },
        },
        {
            name: RouteName.RESET_PASSWORD,
            path: '/resetting/reset/:token',
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/resetting/reset/${to.params.token}`
            },
        },
        {
            name: RouteName.LOGOUT,
            path: '/logout',
            component: () => import(/* webpackChunkName: 'auth' */ '@/views/auth/Logout.vue'),
            meta: {
                layout: NoLayout,
            },
        },
        {
            name: RouteName.BENEFITS,
            path: '/benefits',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/benefits`
            },
        },
        {
            name: RouteName.BENEFITS_FAVORITES,
            path: '/benefits/favorites',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/benefits/favorites`
            },
        },
        {
            name: RouteName.BENEFITS_CATEGORY,
            path: '/benefits/:category',
            beforeEnter(to: Route) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/benefits/${to.params.category}`
            },
        },
        {
            name: RouteName.BENEFITS_SINGLE,
            path: '/benefits/details/:benefit',
            beforeEnter(to: Route) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/benefits/details/${to.params.benefit}`
            },
        },
        {
            name: RouteName.WORKSPACES,
            path: '/offices',
            beforeEnter(to: Route) {
                if (to.query.location) {
                    window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/offices?location=${to.query.location}`
                } else {
                    window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/offices`
                }
            },
        },
        {
            path: '/clubs/:club',
            meta: {
                layoutProps: { extended: 'sm' },
            },
            children: [
                {
                    name: RouteName.CLUB_SINGLE,
                    path: '',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_CHAT,
                    path: 'discussions',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/discussions`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_MEMBERS,
                    path: 'members',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/members`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_BOARD,
                    path: 'board',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/board`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_MARKETPLACE_PROJECTS,
                    path: 'marketplace-projects',
                    redirect() {
                        return {
                            name: RouteName.CLUB_SINGLE_PROJECTS,
                        }
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_STRUCTURED_PROJECTS,
                    path: 'structured-projects',
                    redirect() {
                        return {
                            name: RouteName.CLUB_SINGLE_PROJECTS,
                        }
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_PROJECTS,
                    path: 'projects',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/projects`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_EVENTS,
                    path: 'events',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/events`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_SOLUTIONS_AND_SERVICES,
                    path: 'solutions-and-services',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/solutions-and-services`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_NEWS,
                    path: 'articles',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/articles`
                    },
                },
                {
                    name: RouteName.CLUB_SINGLE_RULES,
                    path: 'rules',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/rules`
                    },
                },

                {
                    name: RouteName.CLUB_SINGLE_VIDEOS,
                    path: 'videos',
                    meta: {
                        layoutProps: { extended: 'sm' },
                    },
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/videos`
                    },
                },
            ],
        },
        {
            name: RouteName.CLUB_SINGLE_JOIN,
            path: '/clubs/:club/join',
            meta: {
                layoutProps: { extended: 'sm', footer: 'no-partners' },
            },
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/join`
            },
        },
        {
            name: RouteName.CLUB_SINGLE_JOIN,
            path: '/clubs/:club/ask-to-join',
            meta: {
                layoutProps: { extended: 'sm', footer: 'no-partners' },
            },
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/ask-to-join`
            },
        },
        {
            path: '/clubs/:club/projects/:project',
            name: RouteName.CLUB_SINGLE_PROJECT_SINGLE,
            meta: {
                layoutProps: { extended: 'sm' },
            },
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/clubs/${to.params.club}/projects/${to.params.project}`
            },
        },
        {
            path: '/hubs/:hub',
            meta: {
                layoutProps: {
                    extended: 'sm',
                },
            },
            children: [
                {
                    name: RouteName.HUB_SINGLE,
                    path: '',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/hubs/${to.params.hub}`
                    },
                    meta: {
                        layoutProps: {
                            extended: 'sm',
                        },
                    },
                },
                {
                    name: RouteName.HUB_SINGLE_CHAT,
                    path: 'discussions',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/hubs/${to.params.hub}/discussions`
                    },
                    meta: {
                        layoutProps: {
                            extended: 'sm',
                        },
                    },
                },
                {
                    name: RouteName.HUB_SINGLE_MEMBERS,
                    path: 'members',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/hubs/${to.params.hub}/members`
                    },
                    meta: {
                        layoutProps: {
                            extended: 'sm',
                        },
                    },
                },
                {
                    name: RouteName.HUB_SINGLE_EVENTS,
                    path: 'events',
                    beforeEnter(to) {
                        window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/hubs/${to.params.hub}/events`
                    },
                    meta: {
                        layoutProps: {
                            extended: 'sm',
                        },
                    },
                },
            ],
        },
        {
            name: RouteName.TRIP_PLANNER_LIST,
            path: '/trips',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/trips`
            },
        },
        {
            name: RouteName.TRIP_PLANNER_ADD,
            path: '/trips/add',
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/trips/add`
            },
        },
        {
            name: RouteName.TRIP_PLANNER_SINGLE,
            path: '/trips/:trip',
            beforeEnter(to: Route) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/trips/${to.params.trip}`
            },
        },
        {
            path: '/unsubscribe',
            name: RouteName.UNSUBSCRIBE,
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_SPA2_BASE_URL}/unsubscribe`
            },
        },
        {
            path: '/nft/NFT_CARD_2022/:idFF',
            name: RouteName.NFT_MEMBER_2022,
            meta: {
                layout: NoLayout,
                allowLead: true,
            },
            component: () => import(/* webpackChunkName: 'nft' */ './views/member/nft/2022/NftViewer.vue'),
        },
        {
            path: '/nft',
            name: RouteName.NFT,
            redirect: { name: RouteName.NFT_HOME },
            meta: {
                layoutProps: {
                    extended: 'xl',
                },
            },
            component: () => import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTHome/NFTHome.vue'),
            children: [
                {
                    path: 'home',
                    name: RouteName.NFT_HOME,
                    meta: {
                        allowCrisp: true,
                    },
                    component: () =>
                        import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTHome/partials/TabMyNFT.vue'),
                },
                {
                    path: 'events',
                    name: RouteName.NFT_EVENTS,
                    meta: {
                        allowCrisp: true,
                    },
                    component: () =>
                        import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTHome/partials/TabEvents.vue'),
                },
                {
                    path: 'faq',
                    name: RouteName.NFT_HELP,
                    meta: {
                        allowCrisp: true,
                    },
                    component: () =>
                        import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTHome/partials/TabHelp.vue'),
                },
                {
                    path: 'wallet',
                    name: RouteName.NFT_WALLET,
                    meta: {
                        allowCrisp: true,
                    },
                    component: () =>
                        import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTHome/partials/TabMyWallet.vue'),
                },
            ],
        },
        {
            path: '/nft/tour',
            name: RouteName.NFT_TOUR,
            meta: {
                allowCrisp: true,
                layoutProps: {
                    extended: 'xl',
                },
            },
            component: () => import(/* webpackChunkName: 'nft' */ './views/member/nft/NFTTour/NFTTour.vue'),
            children: [],
        },
        {
            path: '/team',
            name: RouteName.TEAM_PAGE,
            component: () => import(/* webpackChunkName: 'team-page' */ '@/views/team/Team.vue'),
            meta: {
                layoutProps: {
                    extended: 'sm',
                },
            },
        },
        {
            path: '/articles',
            name: RouteName.ARTICLES,
            meta: {
                layoutProps: {
                    extended: 'xl',
                },
            },
            beforeEnter() {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/articles`
            },
        },
        {
            path: '/article/:id',
            name: RouteName.ARTICLE_PAGE,
            meta: {
                layoutProps: {
                    extended: 'sm',
                },
            },
            beforeEnter(to) {
                window.location.href = `${process.env.VUE_APP_CLUB_BASE_URL}/article/${to.params.id}`
            },
        },
        /*
        {
            path: '*',
            redirect: '/test',
        },
        */
    ],
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
})

router.beforeEach(guard)

router.afterEach((to: Route) => {
    store.commit('app/CLEAR_LAYOUT')
    if (to?.meta?.gtag && window.dataLayer) {
        window.dataLayer.push(to?.meta?.gtag)
    }
})

export default router
